import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Home, Sparkles } from 'lucide-react'

const container = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
}

const item = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] as const },
  },
}

export default function NotFoundPage() {
  const location = useLocation()
  return (
    <div className="relative min-h-[75vh] flex flex-col items-center justify-center pt-24 sm:pt-28 pb-24 px-4 sm:px-6 overflow-hidden">
      {/* Ambient background — digital agency vibe */}
      <div className="absolute inset-0 -z-10">
        <motion.div
          className="absolute top-1/4 left-1/4 w-72 h-72 rounded-full bg-primary/5 dark:bg-primary/10 blur-3xl"
          animate={{
            x: [0, 20, 0],
            y: [0, -15, 0],
            scale: [1, 1.05, 1],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full bg-accent/5 dark:bg-accent/10 blur-3xl"
          animate={{
            x: [0, -15, 0],
            y: [0, 20, 0],
            scale: [1, 1.08, 1],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(120,119,198,0.08),transparent)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(120,119,198,0.12),transparent)]" />
      </div>

      <motion.div
        variants={container}
        initial="hidden"
        animate="visible"
        className="container-wide max-w-xl text-center relative"
      >
        {/* Floating 404 */}
        <motion.div variants={item} className="relative inline-block">
          <motion.span
            className="block text-[clamp(5rem,20vw,11rem)] font-black leading-none tracking-tighter select-none bg-gradient-to-br from-slate-300 via-slate-200 to-slate-300 dark:from-slate-600 dark:via-slate-700 dark:to-slate-600 bg-clip-text text-transparent"
            initial={{ opacity: 0, scale: 0.88 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] }}
            aria-hidden
          >
            404
          </motion.span>
          <motion.span
            className="absolute inset-0 block text-[clamp(5rem,20vw,11rem)] font-black leading-none tracking-tighter select-none text-primary/20 dark:text-accent/25 blur-xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            aria-hidden
          >
            404
          </motion.span>
          <motion.div
            className="absolute -top-1 -right-1 sm:right-2 rounded-full bg-primary/15 dark:bg-accent/25 p-2.5 shadow-lg shadow-primary/10"
            initial={{ opacity: 0, scale: 0, rotate: -20 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ delay: 0.4, type: 'spring', stiffness: 260, damping: 20 }}
          >
            <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-primary dark:text-accent" aria-hidden />
          </motion.div>
        </motion.div>

        <motion.div variants={item} className="mt-2 flex items-center justify-center gap-1.5">
          <span className="text-xs font-medium uppercase tracking-[0.2em] text-primary/80 dark:text-accent/90">
            Digital agency
          </span>
        </motion.div>

        <motion.h1
          variants={item}
          className="mt-4 sm:mt-5 text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white"
        >
          This page went off the grid.
        </motion.h1>
        <motion.p
          variants={item}
          className="mt-3 text-slate-600 dark:text-slate-400 max-w-md mx-auto"
        >
          We craft digital journeys—this one took a wrong turn. Let’s get you back on track.
        </motion.p>

        <motion.div variants={item} className="mt-6">
          <span className="text-xs uppercase tracking-wider text-slate-400 dark:text-slate-500">Requested path</span>
          <motion.span
            className="mt-1.5 block font-mono text-xs sm:text-sm text-slate-600 dark:text-slate-300 bg-slate-100/80 dark:bg-slate-800/80 rounded-xl py-2.5 px-4 break-all border border-slate-200/60 dark:border-slate-700/80"
            whileHover={{ scale: 1.01 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            {location.pathname}
          </motion.span>
        </motion.div>

        <motion.div
          variants={item}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
        >
          <motion.div
            whileHover={{ scale: 1.03, y: -2 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 22 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 btn-primary px-6 py-3 text-sm shadow-lg shadow-primary/20 dark:shadow-primary/10"
            >
              <Home className="w-4 h-4" aria-hidden />
              Back to Home
            </Link>
          </motion.div>
          <motion.div
            whileHover={{ scale: 1.03, y: -2 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 22 }}
          >
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 btn-secondary px-6 py-3 text-sm"
            >
              Get in Touch
            </Link>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  )
}
